import { createContext, PropsWithChildren, useReducer } from "react";
import {
  ChordEditBlastRadius,
  ChordHistoryInteractMode,
  ChordHistoryLabelMode,
  ChordHistoryOptions,
  ChordSoundingArpeggiation,
  ChordSoundingNoteOrder,
  ChordSoundingOscillatorShape,
} from "./models/ChordHistoryOptions.model";
import { buildAutoPinSettingDefaults, KeySelectionOptions } from "./models/KeySelectionOptions.model";
import {
  getVisitorId,
  loadChordHistorySettings,
  loadKeySelectionSettings,
  saveChordHistorySettings,
  saveKeySelectionSettings,
} from "./statemachines/storage";
import { StreamFormat } from "./models/wasm.model";
import { WEBM_PCM_MIME_TYPE } from "./models/AudioStream.interface";

// Centralize certain features like the help pop-up

export enum HelpPageName {
  Welcome,
  Overview,
  TermsOfService,
}
export enum MenuName {
  ChordHistoryOptions,
  KeyIdOptions,
}

export enum AppLoadingState {
  READY,
  NEED_WASM,
  INIT,
  FAILED,
}
export type ErrorState = {
  errorList: Array<string>;
};
export type BrowserFeatureFlags = {
  streamFormat: StreamFormat;
};

type AppContextState = {
  showHelpPopup: boolean;
  helpPageName: HelpPageName;
  showMenu: boolean;
  menuName: MenuName;
  showTranscriptionPopup: boolean;
  chordHistoryOptions: ChordHistoryOptions;
  keySelectionOptions: KeySelectionOptions;
  loading: AppLoadingState;
  errors: ErrorState;
  browser: BrowserFeatureFlags;
  dispatch: (action: AppContextAction) => void;
};

export type AppContextAction =
  | {
      type: "ShowHelpPage";
      payload: HelpPageName;
    }
  | { type: "HideHelpPage"; payload?: undefined }
  | { type: "HideAllPopups"; payload?: undefined }
  | { type: "ShowMenu"; payload: MenuName }
  | { type: "HideMenu"; payload?: undefined }
  | { type: "ShowTranscriptionPopup"; payload?: undefined }
  | { type: "HideTranscriptionPopup"; payload?: undefined }
  | { type: "EditChordHistoryOptions"; payload: Partial<ChordHistoryOptions> }
  | { type: "EditKeySelectionOptions"; payload: Partial<KeySelectionOptions> }
  | { type: "UpdateLoadingState"; payload: AppLoadingState }
  | { type: "AddToErrorList"; payload: Array<string> }
  | { type: "ClearErrorList"; payload?: undefined }
  | { type: "FailoverToRawStream"; payload?: undefined };

const isFirstTimeVisitor = getVisitorId() === null;
const storedKeySelectionSettings = loadKeySelectionSettings();
const storedChordHistorySettings = loadChordHistorySettings();

function buildInitState(): AppContextState {
  return {
    showHelpPopup: isFirstTimeVisitor,
    helpPageName: HelpPageName.Welcome,
    keySelectionOptions: {
      isPreferMajor: false, // plugged in but not editable
      autoPin: buildAutoPinSettingDefaults(),
      ...storedKeySelectionSettings,
    },
    showMenu: false,
    menuName: MenuName.ChordHistoryOptions,
    showTranscriptionPopup: false,
    chordHistoryOptions: {
      // FIXME: localstorage - maintain user preferences
      interactMode: ChordHistoryInteractMode.SOUND,
      labelMode: ChordHistoryLabelMode.RootLetter,
      autoScroll: true,
      sounding: {
        arpeggiation: ChordSoundingArpeggiation.Slow,
        ordering: ChordSoundingNoteOrder.StartFromRoot,
      },
      edits: {
        blastRadius: ChordEditBlastRadius.OnlyThisInstance,
      },
      noteRow: {
        hideNoteOctave: false,
        // warnIfDeleteZeroesChords: true,
        hideNotes: false,
        oscillatorShape: ChordSoundingOscillatorShape.SINE,
      },
      ...storedChordHistorySettings,
    },
    loading: AppLoadingState.INIT,
    errors: {
      errorList: [],
    },
    browser: {
      streamFormat: MediaRecorder.isTypeSupported(WEBM_PCM_MIME_TYPE) ? "WEBM_PCM" : "RAW",
    },
    /**
     * Weird - this implementation of dispatch is never seen by the components,
     * it's overridden by feeding the real dispatch directly into the AppContextType.
     *
     * React wants a separate context provider for dispatch, which is stupid.
     */
    dispatch: (_action: AppContextAction) => console.warn("unreachable default dispatch was used"),
  };
}

const appContextReducer = (oldState: AppContextState, action: AppContextAction): AppContextState => {
  switch (action.type) {
    case "HideAllPopups": {
      return {
        ...oldState,
        showHelpPopup: false,
        showMenu: false,
      };
    }
    case "ShowHelpPage":
      return {
        ...oldState,
        showHelpPopup: true,
        helpPageName: action.payload,
      };
    case "HideHelpPage":
      return {
        ...oldState,
        showHelpPopup: false,
      };
    case "ShowMenu":
      return {
        ...oldState,
        showMenu: true,
        menuName: action.payload,
      };
    case "HideMenu":
      return {
        ...oldState,
        showMenu: false,
      };
    case "ShowTranscriptionPopup":
      return {
        ...oldState,
        showTranscriptionPopup: true,
      };
    case "HideTranscriptionPopup":
      return {
        ...oldState,
        showTranscriptionPopup: false,
      };
    case "EditChordHistoryOptions":
      saveChordHistorySettings({
        ...oldState.chordHistoryOptions,
        ...action.payload,
      });
      return {
        ...oldState,
        chordHistoryOptions: {
          ...oldState.chordHistoryOptions,
          ...action.payload,
        },
      };
    case "EditKeySelectionOptions": {
      saveKeySelectionSettings({
        ...oldState.keySelectionOptions,
        ...action.payload,
      });
      return {
        ...oldState,
        keySelectionOptions: {
          ...oldState.keySelectionOptions,
          ...action.payload,
        },
      };
    }
    case "UpdateLoadingState": {
      return {
        ...oldState,
        loading: action.payload,
      };
    }
    case "AddToErrorList": {
      return {
        ...oldState,
        errors: {
          ...oldState.errors,
          errorList: [...oldState.errors.errorList, ...action.payload],
        },
      };
    }
    case "ClearErrorList": {
      return {
        ...oldState,
        errors: {
          ...oldState.errors,
          errorList: [],
        },
      };
    }
    case "FailoverToRawStream": {
      return {
        ...oldState,
        browser: {
          ...oldState.browser,
          streamFormat: 'RAW',
        }
      }
    }
    default:
      return oldState;
  }
};

export const AppContextType = createContext<AppContextState>(buildInitState());

export function AppContextProvider({ children }: PropsWithChildren) {
  // function CounterProvider({ children }) {
  const [state, dispatch] = useReducer(appContextReducer, buildInitState());

  return <AppContextType value={{ ...state, dispatch }}>{children}</AppContextType>;
}
